import { ANNOTATION_COLORS, ANNOTATION_OPACITY } from "@perceptron/constants/colors";
import { config } from "react-spring";

import { toPresentationUs } from "./utils/time";

// ============================================================================
// Layout & Dimensions
// ============================================================================

/** Height of the timeline ruler in pixels; the top leaves room for the zoomed-in overview strip */
export const RULER_HEIGHT = 33;

/** Height of collection header rows in pixels */
export const ROW_HEIGHT_HEADER = 32;

/** Height of track rows in pixels */
export const ROW_HEIGHT_TRACK = 32;

/** Scrub auto-scroll speed (the old 15 px per frame at 60 Hz) */
export const SCROLL_SPEED_PX_PER_SECOND = 900;

/** Buffer size in pixels for viewport rendering optimization */
export const VIEWPORT_BUFFER_PX = 200;

// ============================================================================
// Timeline & Zoom
// ============================================================================

/** Minimum zoom level (pixels per second) */
export const MIN_PIXELS_PER_SECOND = 0.1;

/** Maximum zoom level (pixels per second) */
export const MAX_PIXELS_PER_SECOND = 1000;

/**
 * Timeline width as a fraction of the viewport at max zoom-out (0.95 = 5% padding on the right).
 * The same padding stays past the end when zoomed in, so the last frame is never flush with the edge.
 */
export const TIMELINE_VIEWPORT_RATIO = 0.95;

/** Snap threshold in pixels for segment snapping */
export const SNAP_THRESHOLD_PX = 8;

/** Target spacing in pixels between major ruler ticks */
export const RULER_TICK_SPACING_TARGET_PX = 120;

/** Major ruler ticks never get closer than this, so labels can't collide */
export const MIN_MAJOR_TICK_SPACING_PX = 80;

/** Minor ruler ticks never get closer than this */
export const MIN_MINOR_TICK_SPACING_PX = 8;

/** Height of minor ruler ticks in pixels */
export const MINOR_TICK_HEIGHT = 5;

/** Height of major ruler ticks in pixels */
export const MAJOR_TICK_HEIGHT = 10;

/** Gradient start color for ruler ticks (bottom, transparent) */
export const TICK_GRADIENT_START = "rgba(153, 153, 153, 0.2)";

/** Gradient end color for ruler ticks (top, opaque) */
export const TICK_GRADIENT_END = "#D0D0D0";

// ============================================================================
// Colors
// ============================================================================

/** Color for timeline ruler text */
export const TEXT_COLOR = "#E5E7EB";

/** Text color for timeline ruler */
export const TIMELINE_RULER_TEXT_COLOR = "#FFFFFF99";

/** Color for track row borders */
export const TRACK_BORDER_COLOR = "rgba(255, 255, 255, 0.1)";

/** Bright crest of the annotation density summary */
export const SUMMARY_TRACK_COLOR_BASE = "rgba(255, 255, 255, 0.8)";

/** Transparent foot of the annotation density summary */
export const SUMMARY_TRACK_COLOR_LIGHT = "rgba(255, 255, 255, 0)";

/** Color for clip segment backgrounds (blue) */
export const SEGMENT_COLOR_BASE = "#144D7F";
export const SEGMENT_COLOR_LIGHT = "#2B89E8";

/** Color for point_box segment backgrounds (orange) */
export const POINT_BOX_COLOR_BASE = "#A95A0B";
export const POINT_BOX_COLOR_LIGHT = "#D4781A";

/**
 * Track-bar colors. Aliased to the point_box palette but declared separately
 * so future track-specific tweaks don't disturb `point_box` keyframe styling.
 */
export const TRACK_COLOR_BASE = POINT_BOX_COLOR_BASE;
export const TRACK_COLOR_LIGHT = POINT_BOX_COLOR_LIGHT;

/** Color for video track progress bar */
export const VIDEO_TRACK_COLOR_BASE = "#D9E3F4";

/** Color for played portion of video track */
export const VIDEO_TRACK_PLAYED_COLOR = "#FFFFFF";

/** Color for unplayed portion of video track */
export const VIDEO_TRACK_UNPLAYED_COLOR = "#2A2B2D";

export const SEGMENT_STROKE_COLOR = "hsla(30, 6%, 7%, 1)";

// ============================================================================
// Text & Sizing
// ============================================================================

/** Width allocated for timeline text labels */
export const TEXT_WIDTH = 60;

/** Minimum segment width in pixels before hiding segment entirely */
export const MIN_SEGMENT_WIDTH_PX = 0.5;

/** Size of diamond marker for point-in-time segments (segments without end time) */
export const POINT_MARKER_SIZE = 12;

// ============================================================================
// Padding & Spacing
// ============================================================================

/** Vertical padding for segment rectangles */
export const PADDING_Y = 4;

/** General padding for summary track */
export const PADDING = 4;

/** Height of the low bar in summary track */
export const LOW_BAR_HEIGHT = 4;

/** Corner radius for rounded elements */
export const CORNER_RADIUS = 4;

// ============================================================================
// Media Controls
// ============================================================================

/** Skip amount in seconds for skip forward/backward */
export const SKIP_AMOUNT = 5;

/** Seek amount in seconds for rewind/fast-forward */
export const SEEK_AMOUNT = 10;

// ============================================================================
// Playhead
// ============================================================================

/** Width of the playhead glow gradient in pixels */
export const PLAYHEAD_GLOW_WIDTH = 100;

/** Height of the playhead glow gradient in pixels */
export const PLAYHEAD_GLOW_HEIGHT = 16;

/** Shadow blur radius for playhead elements */
export const PLAYHEAD_SHADOW_BLUR = 12;

/** Shadow X offset for playhead elements */
export const PLAYHEAD_SHADOW_OFFSET_X = -4;

/** Shadow opacity for the main playhead line */
export const PLAYHEAD_SHADOW_OPACITY = 0.95;

/** Shadow opacity for the playhead pentagon marker */
export const PLAYHEAD_PENTAGON_SHADOW_OPACITY = 0.5;

/** Gradient colors for playhead glow effect (left to right) */
export const PLAYHEAD_GLOW_COLORS = ["#FFFFFF", "#DDDB97", "#DE8933", "#E1765B"] as const;

/** SVG path data for the playhead pentagon marker */
export const PLAYHEAD_PENTAGON_PATH =
  "M 3.88889 0 H -3.88889 C -4.50253 0 -5 0.49747 -5 1.11111 V 12.77778 C -5 13.12756 -4.83534 13.45678 -4.55556 13.66667 L -0.85186 16.44444 C -0.65952 16.58867 -0.4256 16.66667 -0.18519 16.66667 H 0.18519 C 0.4256 16.66667 0.65952 16.58867 0.85186 16.44444 L 4.55556 13.66667 C 4.83533 13.45678 5 13.12756 5 12.77778 V 1.11111 C 5 0.49747 4.50256 0 3.88889 0 Z";

// ============================================================================
// Animation
// ============================================================================

/** Animation configuration for timeline transitions */
export const ANIM_CONFIG = { ...config.stiff, duration: 150 };

// ============================================================================
// Glow
// ============================================================================

export const GLOW_BLUR = 6;
export const GLOW_OPACITY = 0.85;
export const GLOW_COLOR = SEGMENT_COLOR_BASE;
export const DIMMED_OPACITY = 0.4;
export const ANIMATION_DURATION = 0.25;

// ============================================================================
// Bounding Box Overlay
// ============================================================================

/** Maximum anchor gap for both timeline bars and tracking interpolation, in seconds. */
export const TRACK_MAX_GAP_SECONDS = 1;

/** Compare at the decoded media clock's microsecond precision without changing GT timestamps. */
export function isWithinTrackGap(start: number, end: number): boolean {
  return toPresentationUs(end) - toPresentationUs(start) <= toPresentationUs(TRACK_MAX_GAP_SECONDS);
}

/** Brief visibility window for timestamp-only annotations, without changing their coordinates. */
export const TIMESTAMP_DISPLAY_DURATION = 0.067;

/**
 * Spatial boxes drawn over video, matched to the ones drawn over images so the
 * same annotation does not look heavier on one than the other.
 *
 * Deliberately NOT `POINT_BOX_COLOR_BASE`: that still drives the timeline bars
 * and label-pane icons, which are a different surface and stay as they are.
 */
export const BOUNDING_BOX = {
  FILL_COLOR: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, ${ANNOTATION_OPACITY.FILL_DEFAULT})`,
  STROKE_COLOR: ANNOTATION_COLORS.PRIMARY,
  STROKE_WIDTH: 2,
  CORNER_RADIUS: 2,
} as const;
